import {MobileDrawerWrapper} from '@wix/wixstores-client-storefront-common/components';
import {ProductOptionType} from '@wix/wixstores-graphql-schema/dist/es/src';
import React, {useCallback} from 'react';
import stylesParams from '../../../../../components/ProductPage/stylesParams';
import {TextualOptionType} from '../../../../constants';
import {useGlobals} from '../../../../providers/globalPropsProvider';
import {OptionsButtons} from '../../../ProductOptions/OptionsButton/OptionsButtons';
import {OptionsDropdown} from '../../../ProductOptions/OptionsDropdown/OptionsDropdown';
import {ProductColors} from '../../../ProductOptions/ProductColors/ProductColors';
import {ProductCustomText} from '../../../ProductOptions/ProductCustomText/ProductCustomText';
import {StickyButton} from '../StickyButton/StickyButton';
import s from './StickyOptionsDrawer.scss';
import {classes} from './StickyOptionsDrawer.st.css';

export enum StickyOptionsDrawerDataHook {
  Root = 'StickyOptionsDrawerDataHook.Root',
  Content = 'StickyOptionsDrawerContentDataHook.Root',
  Options = 'StickyOptionsDrawerContentDataHook.Options',
  AddToCartButton = 'StickyOptionsDrawerContentDataHook.AddToCartButton',
}

export type StickyOptionsDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  buttonText: string;
};

export const StickyOptionsDrawer = ({isOpen, onClose, buttonText}: StickyOptionsDrawerProps) => {
  const {host, product, handleAddToCart, styles, isUserInputsValid} = useGlobals();

  const textualOptionType = styles.get(stylesParams.productPage_textualOptionType);
  const useButtons = textualOptionType === TextualOptionType.BUTTONS;

  const handleAddToCartClick = useCallback(() => {
    handleAddToCart();
    onClose();
  }, [handleAddToCart, onClose]);

  function renderOption(option: (typeof product.options)[number], index: number) {
    if (option.optionType === ProductOptionType.COLOR) {
      return (
        <div className={s.optionContainer} key={`option-${index}`}>
          <ProductColors optionIndex={index} />
        </div>
      );
    }

    return (
      <div className={s.optionContainer} key={`option-${index}`}>
        {useButtons ? <OptionsButtons optionIndex={index} /> : <OptionsDropdown optionIndex={index} />}
      </div>
    );
  }

  function renderCustomTextFields() {
    const customTextFields = product?.customTextFields;
    if (!customTextFields?.length) {
      return null;
    }

    return customTextFields.map((customTextField, index) => (
      <div className={s.optionContainer} key={`custom-text-${index}`}>
        <ProductCustomText
          errorIndex={index}
          title={customTextField.title}
          isRequired={customTextField.isMandatory}
          maxLength={customTextField.inputLimit}
          className={classes.compactTextArea}
        />
      </div>
    ));
  }

  const drawerButtonText = isUserInputsValid ? buttonText : 'Select options';
  const disabled = !isUserInputsValid;

  return (
    <MobileDrawerWrapper
      isOpen={isOpen}
      onRequestClose={onClose}
      dataHook={StickyOptionsDrawerDataHook.Root}
      hostId={host.id}>
      <div className={s.root} data-hook={StickyOptionsDrawerDataHook.Content}>
        <div className={s.scrollableContainer} data-hook={StickyOptionsDrawerDataHook.Options}>
          {product?.options?.map(renderOption)}
          {renderCustomTextFields()}
        </div>
        <div className={s.addToCartButtonContainer}>
          <StickyButton text={drawerButtonText} onClick={handleAddToCartClick} disabled={disabled} />
        </div>
      </div>
    </MobileDrawerWrapper>
  );
};
